// Refresh feedback is source-specific: gesture pulls render the branch
// indicator, while initial loading and nav/search-triggered reloads light the
// SearchHeader border. The floating capsule carries every active refresh once
// the header scrolls away.

// Who initiated the refresh. 'gesture' = a real pull (wheel or touch);
// 'nav' = PanelState.triggerStreamRefresh (sidebar/nav clicks, floating filter);
// 'search' = SearchHeader/Search/Streams/Memes driving setIsRefreshing directly.
export type RefreshSource = 'gesture' | 'nav' | 'search'

export interface RefreshState {
    isRefreshing: boolean
    source: RefreshSource | null
}

export type RefreshStateUpdate = boolean | ((isRefreshing: boolean) => boolean)

export function idleRefreshState(): RefreshState {
    return { isRefreshing: false, source: null }
}

export function activeRefreshState(source: RefreshSource): RefreshState {
    return { isRefreshing: true, source }
}

// Header/query effects share the panel's refresh state. Keep their transition
// pure so React can safely replay it, and preserve an explicit gesture/nav
// source when a query observer echoes `true` for the same in-flight refresh.
export function updateHeaderRefreshState(
    current: RefreshState,
    update: RefreshStateUpdate,
): RefreshState {
    const isRefreshing = typeof update === 'function' ? update(current.isRefreshing) : update

    if (!isRefreshing) {
        return current.isRefreshing || current.source ? idleRefreshState() : current
    }

    const source = current.isRefreshing ? current.source ?? 'search' : 'search'
    if (current.isRefreshing && current.source === source) return current

    return { isRefreshing: true, source }
}

export function resolveRefreshPresentation({
    isInitialLoading = false,
    isRefreshing,
    source,
}: {
    isInitialLoading?: boolean
    isRefreshing: boolean
    source: RefreshSource | null
}) {
    const showProgrammaticFeedback = isInitialLoading || (isRefreshing && source !== 'gesture')

    return {
        showPullIndicator: isRefreshing && source === 'gesture',
        showProgrammaticFeedback,
        showCapsuleHalo: isRefreshing,
    }
}
